import React, { useEffect } from 'react';
import { Button, Descriptions, Drawer, Form, Input, InputNumber, Select, Space, Tag, Typography } from 'antd';
import { formatMoney } from '@/utils/format';
import { getEst3CostProvenance } from './est3CostProvenance';
import { EpcEstimateWbsTreeSelect } from './EpcEstimateWbsTreeSelect';

const { Text } = Typography;

export interface EpcEstimateCostLineDrawerProps {
  line: Record<string, unknown> | null;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
  wbsLabel?: string;
  cbsLabel?: string;
  wbsOptions?: Array<{ id: string; code: string; name?: string; parent_id?: string | null }>;
  cbsOptions?: Array<{ id: string; code: string; name?: string; parent_id?: string | null }>;
  boqLabel?: string;
  boqOptions?: Array<{ id: string; item_number?: string; description?: string }>;
  onPatch?: (lineId: string, patch: Record<string, unknown>) => Promise<void>;
  patchPending?: boolean;
  onOpenMaterials?: (lineId: string) => void;
  onOpenLabour?: (lineId: string) => void;
  onOpenEquipment?: (lineId: string) => void;
}

export const EpcEstimateCostLineDrawer: React.FC<EpcEstimateCostLineDrawerProps> = ({
  line,
  open,
  onClose,
  readOnly,
  wbsLabel,
  cbsLabel,
  wbsOptions = [],
  cbsOptions = [],
  boqLabel,
  boqOptions = [],
  onPatch,
  patchPending,
  onOpenMaterials,
  onOpenLabour,
  onOpenEquipment,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (line) {
      form.setFieldsValue({
        clientBoqLineId: line.client_boq_line_id || undefined,
        wbsNodeId: line.wbs_node_id || undefined,
        cbsCodeId: line.cbs_code_id || undefined,
        description: line.description,
        quantity: line.quantity,
        rate: line.rate,
        uom: line.uom,
        wasteFactor: line.waste_factor,
        productivityFactor: line.productivity_factor,
      });
    }
  }, [line, form]);

  if (!line) return null;
  const id = String(line.id || '');
  const kind = String(line.resource_kind || '');
  const prov = getEst3CostProvenance(line);

  const save = async () => {
    if (!onPatch || !id) return;
    const v = await form.validateFields();
    await onPatch(id, {
      clientBoqLineId: v.clientBoqLineId ?? null,
      wbsNodeId: v.wbsNodeId,
      cbsCodeId: v.cbsCodeId,
      description: v.description,
      quantity: v.quantity,
      rate: v.rate,
      uom: v.uom,
      wasteFactor: v.wasteFactor,
      productivityFactor: v.productivityFactor,
    });
    onClose();
  };

  return (
    <Drawer
      title="Cost line detail"
      open={open}
      onClose={onClose}
      width={520}
      extra={
        !readOnly && onPatch ? (
          <Button type="primary" loading={patchPending} onClick={() => void save()}>
            Save
          </Button>
        ) : null
      }
    >
      {readOnly ? (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Description">{String(line.description || '—')}</Descriptions.Item>
          <Descriptions.Item label="Resource kind">
            <Tag>{kind || '—'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="WBS">{wbsLabel || '—'}</Descriptions.Item>
          <Descriptions.Item label="CBS">{cbsLabel || '—'}</Descriptions.Item>
          <Descriptions.Item label="Client BOQ">{boqLabel || '—'}</Descriptions.Item>
          <Descriptions.Item label="Quantity">{String(line.quantity ?? '—')}</Descriptions.Item>
          <Descriptions.Item label="Rate">
            {formatMoney(line.rate, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
          <Descriptions.Item label="Total">
            {formatMoney(line.total_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Resource kind">
            <Tag>{kind || '—'}</Tag>
          </Form.Item>
          <Form.Item name="wbsNodeId" label="WBS" rules={[{ required: true }]}>
            <EpcEstimateWbsTreeSelect nodes={wbsOptions} />
          </Form.Item>
          <Form.Item name="cbsCodeId" label="CBS" rules={[{ required: true }]}>
            <EpcEstimateWbsTreeSelect nodes={cbsOptions} kind="cbs" />
          </Form.Item>
          <Form.Item name="clientBoqLineId" label="Client BOQ">
            <Select
              allowClear
              placeholder="Link to BOQ line"
              options={boqOptions.map((b) => ({
                value: b.id,
                label: b.item_number || b.description || b.id.slice(0, 8),
              }))}
            />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="uom" label="UoM">
            <Input />
          </Form.Item>
          <Form.Item name="rate" label="Rate">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {kind === 'MATERIAL' ? (
            <Form.Item name="wasteFactor" label="Waste factor (%)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          {kind === 'LABOUR' ? (
            <Form.Item name="productivityFactor" label="Productivity factor">
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Descriptions.Item label="Total">
            {formatMoney(line.total_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
        </Form>
      )}

      <Descriptions
        title="Provenance"
        size="small"
        column={1}
        bordered
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Rate source">{prov.rateSource || '—'}</Descriptions.Item>
        <Descriptions.Item label="Ref type">{prov.sourceRefType || '—'}</Descriptions.Item>
        <Descriptions.Item label="Ref label">{prov.sourceRefLabel || '—'}</Descriptions.Item>
        <Descriptions.Item label="Ref id">
          <Text copyable={!!prov.sourceRefId}>{prov.sourceRefId || '—'}</Text>
        </Descriptions.Item>
      </Descriptions>

      {!readOnly && id ? (
        <Space style={{ marginTop: 16 }} wrap>
          {(kind === 'MATERIAL' || !kind) && onOpenMaterials ? (
            <Button size="small" onClick={() => onOpenMaterials(id)}>
              Materials build-up
            </Button>
          ) : null}
          {kind === 'LABOUR' && onOpenLabour ? (
            <Button size="small" onClick={() => onOpenLabour(id)}>
              Labour / crew
            </Button>
          ) : null}
          {kind === 'EQUIPMENT' && onOpenEquipment ? (
            <Button size="small" onClick={() => onOpenEquipment(id)}>
              Equipment components
            </Button>
          ) : null}
        </Space>
      ) : null}
    </Drawer>
  );
};
